/**
 * The access queue, read once for the whole app.
 *
 * `links/{uid}` is server-owned — `firestore.rules` lets a signed-in user read his OWN row and
 * nothing else — so the admin's view of who is waiting cannot come from the store like everything
 * else on this screen does. It comes from `GET /api/links`, which checks the caller is an admin.
 *
 * That is also why this is a hook rather than part of `useChrome`: the shell's chrome is built from
 * store subscriptions that every signed-in manager has, and this is one HTTP request that only an
 * admin is allowed to make. Anybody else gets an empty queue without a request being sent.
 */

import { useCallback, useEffect, useState } from 'react';
import { waitingCount, type AccessLink } from '../domain/access.js';
import { idToken } from '../store/session.js';
import { atLeast, type Role } from '../store/types.js';

export interface AccessQueue {
  links: AccessLink[] | null;
  waiting: number;
  /** null while unknown, false offline — the single-file build has no server to ask. */
  token: string | null | undefined;
  error: string | null;
  reload: () => Promise<void>;
}

export function useAccessRequests(role: Role): AccessQueue {
  const admin = atLeast(role, 'admin');
  const [token, setToken] = useState<string | null | undefined>(undefined);
  const [links, setLinks] = useState<AccessLink[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!admin) { setToken(null); return undefined; }
    let live = true;
    void idToken().then((t) => { if (live) setToken(t); });
    return () => { live = false; };
  }, [admin]);

  const reload = useCallback(async () => {
    if (!admin || !token) return;
    try {
      const res = await fetch('/api/links', { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error((await res.json() as { error?: string }).error ?? `HTTP ${res.status}`);
      setLinks((await res.json() as { links: AccessLink[] }).links);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read the accounts.');
    }
  }, [admin, token]);

  useEffect(() => { void reload(); }, [reload]);

  return { links, waiting: links ? waitingCount(links) : 0, token, error, reload };
}
